/**
 * errors.js — Structured error system for Makedo SDK.
 *
 * Exports:
 *   MakedoError     — Error subclass with code, context, retriable flag.
 *   ErrorCodes      — Frozen catalog of all known error code strings.
 *   fromMediaError() — Maps getUserMedia / getDisplayMedia DOMExceptions to MakedoError.
 *   fromHttpError()  — Maps HTTP response failures to MakedoError.
 */

// ─── Error Codes ────────────────────────────────────────────────────────────

const ErrorCodes = Object.freeze({

    // Initialization
    INIT_REQUIRED:              'INIT_REQUIRED',
    INIT_INVALID_CONFIG:        'INIT_INVALID_CONFIG',

    // Authentication
    AUTH_FAILED:                'AUTH_FAILED',
    AUTH_SESSION_EXPIRED:       'AUTH_SESSION_EXPIRED',
    AUTH_TOKEN_REFRESH_FAILED:  'AUTH_TOKEN_REFRESH_FAILED',
    AUTH_PROXY_FAILED:          'AUTH_PROXY_FAILED',
    AUTH_FORBIDDEN:             'AUTH_FORBIDDEN',
    PROJECT_UNAVAILABLE:        'PROJECT_UNAVAILABLE',

    // Media — camera / microphone
    MEDIA_PERMISSION_DENIED:    'MEDIA_PERMISSION_DENIED',
    MEDIA_DEVICE_BUSY:          'MEDIA_DEVICE_BUSY',
    MEDIA_NOT_FOUND:            'MEDIA_NOT_FOUND',
    MEDIA_OVERCONSTRAINED:      'MEDIA_OVERCONSTRAINED',
    MEDIA_TRACK_REPLACE_FAILED: 'MEDIA_TRACK_REPLACE_FAILED',
    MEDIA_TRACK_ENDED:          'MEDIA_TRACK_ENDED',
    MEDIA_UNKNOWN:              'MEDIA_UNKNOWN',

    // Media — screenshare
    SCREENSHARE_PERMISSION_DENIED: 'SCREENSHARE_PERMISSION_DENIED',
    SCREENSHARE_UNSUPPORTED:       'SCREENSHARE_UNSUPPORTED',
    SCREENSHARE_CANCELLED:         'SCREENSHARE_CANCELLED',
    SCREENSHARE_UNKNOWN:           'SCREENSHARE_UNKNOWN',

    // Room / channel
    ROOM_NOT_FOUND:             'ROOM_NOT_FOUND',
    ROOM_CLOSED:                'ROOM_CLOSED',
    ROOM_JOIN_FAILED:           'ROOM_JOIN_FAILED',
    ROOM_FULL:                  'ROOM_FULL',
    ROOM_DISABLED:              'ROOM_DISABLED',
    ROOM_KICKED:                'ROOM_KICKED',
    ROOM_SFU_UNREACHABLE:       'ROOM_SFU_UNREACHABLE',
    ACCOUNT_LIMIT_REACHED:      'ACCOUNT_LIMIT_REACHED',

    // Network — HTTP
    NETWORK_HTTP_ERROR:         'NETWORK_HTTP_ERROR',
    NETWORK_TIMEOUT:            'NETWORK_TIMEOUT',
    NETWORK_OFFLINE:            'NETWORK_OFFLINE',

    // Network — WebSocket
    NETWORK_WS_AUTH_FAILED:     'NETWORK_WS_AUTH_FAILED',
    NETWORK_WS_DISCONNECTED:    'NETWORK_WS_DISCONNECTED',

    // State guards
    STATE_NOT_LOGGED_IN:        'STATE_NOT_LOGGED_IN',
    STATE_NOT_IN_ROOM:          'STATE_NOT_IN_ROOM',
    STATE_ALREADY_LIVE:         'STATE_ALREADY_LIVE',
    STATE_NO_ELEMENT:           'STATE_NO_ELEMENT',

    // Validation
    INVALID_PARAM:              'INVALID_PARAM',
    DUPLICATE_STREAM_LABEL:     'DUPLICATE_STREAM_LABEL',

    // Video pipeline — derived (tapped) streams and, later, processors
    PIPELINE_SOURCE_UNAVAILABLE: 'PIPELINE_SOURCE_UNAVAILABLE',
    PIPELINE_UNSUPPORTED:        'PIPELINE_UNSUPPORTED',
    PROCESSOR_UNSUPPORTED:       'PROCESSOR_UNSUPPORTED',

    // Transcription
    TRANSCRIPTION_UNSUPPORTED:  'TRANSCRIPTION_UNSUPPORTED',
    AUDIO_UPLOAD_FAILED:        'AUDIO_UPLOAD_FAILED',
});


// ─── Hints ──────────────────────────────────────────────────────────────────

const HINTS = Object.freeze({
    [ErrorCodes.MEDIA_PERMISSION_DENIED]:
        'The user denied camera/microphone access, or the browser blocked it. Check site permissions.',
    [ErrorCodes.MEDIA_DEVICE_BUSY]:
        'The camera or microphone is in use by another application. Close other apps that may be using it.',
    [ErrorCodes.MEDIA_NOT_FOUND]:
        'No camera or microphone was detected. Check that a device is connected.',
    [ErrorCodes.MEDIA_OVERCONSTRAINED]:
        'The requested resolution or frame rate is not supported by the device. Try lower constraints.',
    [ErrorCodes.SCREENSHARE_PERMISSION_DENIED]:
        'Screen sharing was denied by the user or browser policy.',
    [ErrorCodes.SCREENSHARE_CANCELLED]:
        'The user cancelled the screen-share picker dialog.',
    [ErrorCodes.SCREENSHARE_UNSUPPORTED]:
        'This browser does not support screen sharing (getDisplayMedia).',
    [ErrorCodes.AUTH_SESSION_EXPIRED]:
        'Your session has expired. Call signup() or login() again.',
    [ErrorCodes.AUTH_TOKEN_REFRESH_FAILED]:
        'Automatic token refresh failed. The user needs to re-authenticate.',
    [ErrorCodes.AUTH_FORBIDDEN]:
        'The server rejected authorization. Check your projectId, projectKey, or proxy configuration.',
    [ErrorCodes.PROJECT_UNAVAILABLE]:
        'The projectId was not found. Check VibeLive.init({ projectId }) — the project may have been deleted or the ID is incorrect.',
    [ErrorCodes.AUTH_PROXY_FAILED]:
        'The authorization proxy returned an error. Verify the proxy URL and that it forwards credentials correctly.',
    [ErrorCodes.NETWORK_WS_DISCONNECTED]:
        'The real-time connection was lost. Member state updates will not arrive until reconnected.',
    [ErrorCodes.NETWORK_WS_AUTH_FAILED]:
        'WebSocket authentication failed. The auth token may be invalid or expired.',
    [ErrorCodes.ROOM_SFU_UNREACHABLE]:
        'Cannot reach the video server. Check network connectivity and firewall rules.',
    [ErrorCodes.ROOM_KICKED]:
        'You were removed from the room by the host.',
    [ErrorCodes.STATE_NO_ELEMENT]:
        'No <video> element registered. Call setLocalCamera() / setRemoteCamera() before starting media.',
    [ErrorCodes.INIT_REQUIRED]:
        'VibeLive.init() was not called. Call VibeLive.init({ projectId }) before using any methods.',
    [ErrorCodes.STATE_NOT_LOGGED_IN]:
        'No user session. Call signup() or login() and await the result before this method.',
    [ErrorCodes.INVALID_PARAM]:
        'A required parameter was missing or invalid. Check the method signature in the API docs.',
    [ErrorCodes.DUPLICATE_STREAM_LABEL]:
        'A stream with this label is already published. Each simultaneous upstream needs a unique label; unpublishStream() the old one first or choose a different label.',
    [ErrorCodes.PIPELINE_SOURCE_UNAVAILABLE]:
        'publishStream({from}) needs the source label to already have a live video track. Turn the camera on (setVideo(true)) before publishing a stream derived from it.',
    [ErrorCodes.PIPELINE_UNSUPPORTED]:
        'This browser cannot produce a MediaStream from a canvas (canvas.captureStream), which derived streams require.',
    [ErrorCodes.PROCESSOR_UNSUPPORTED]:
        'Video processors are not available in this SDK version. Derived streams via publishStream({from}) are supported; per-frame processing is not yet.',
    [ErrorCodes.ROOM_NOT_FOUND]:
        'The room was not found. It may have been deleted or the ID/code is incorrect.',
    [ErrorCodes.ROOM_CLOSED]:
        'This room exists but is not currently accepting participants.',
    [ErrorCodes.ROOM_JOIN_FAILED]:
        'Could not join the room. The server rejected the membership request.',
    [ErrorCodes.ROOM_FULL]:
        'The room is at capacity. Wait for a participant to leave or ask the host to increase the limit.',
    [ErrorCodes.ACCOUNT_LIMIT_REACHED]:
        'This account has reached its concurrent-user hosting limit. Wait for other participants to leave or contact the account owner.',
    [ErrorCodes.ROOM_DISABLED]:
        'This room has been disabled by its owner and is not accepting participants.',
    [ErrorCodes.TRANSCRIPTION_UNSUPPORTED]:
        'Transcription is not enabled for this project/room, or the browser cannot record audio.',
    [ErrorCodes.AUDIO_UPLOAD_FAILED]:
        'A recorded audio segment could not be uploaded. Check connectivity and that the user is signed in.',
});


// ─── MakedoError ────────────────────────────────────────────────────────────

class MakedoError extends Error {

    /**
     * @param {string} code      — One of ErrorCodes.* (e.g. 'MEDIA_DEVICE_BUSY')
     * @param {string} message   — Human-readable description of what went wrong
     * @param {Object} [options]
     * @param {Error}  [options.cause]    — The original lower-level error
     * @param {string} [options.context]  — SDK method where the error occurred (e.g. 'toggleVideo')
     * @param {boolean}[options.retriable]— Whether the operation can be retried
     * @param {string} [options.reason]   — Structured why-code for kick/removal events (e.g. 'session_timeout', 'host_removed')
     * @param {string} [options.source]   — Who initiated it: 'system' | 'host'
     */
    constructor(code, message, { cause, context, retriable, subcode, reason, source } = {}) {
        super(message, { cause });
        this.name = 'MakedoError';
        this.code = code;
        this.subcode = subcode ?? null;
        this.context = context ?? null;
        this.retriable = retriable ?? null;
        this.reason = reason ?? null;
        this.source = source ?? null;
        this.hint = HINTS[code] ?? null;
        this.timestamp = Date.now();
    }

    /** Full diagnostic string — designed to be copy-pasted to a debugging assistant. */
    toString() {
        const lines = [`[${this.code}] ${this.message}`];
        if (this.subcode)   lines.push(`  subcode:   ${this.subcode}`);
        if (this.reason)    lines.push(`  reason:    ${this.reason}`);
        if (this.source)    lines.push(`  source:    ${this.source}`);
        if (this.context)   lines.push(`  context:   ${this.context}`);
        if (this.hint)      lines.push(`  hint:      ${this.hint}`);
        if (this.retriable !== null) lines.push(`  retriable: ${this.retriable}`);
        if (this.cause) {
            const causeName = this.cause.name ? `${this.cause.name}: ` : '';
            lines.push(`  cause:     ${causeName}${this.cause.message || this.cause}`);
        }
        if (this.serverBody) {
            try { lines.push(`  server:    ${JSON.stringify(this.serverBody)}`); } catch (_) {}
        }
        return lines.join('\n');
    }

    /** Plain object safe for JSON.stringify / logging. */
    toJSON() {
        return {
            name: this.name,
            code: this.code,
            subcode: this.subcode ?? null,
            message: this.message,
            reason: this.reason ?? null,
            source: this.source ?? null,
            context: this.context,
            retriable: this.retriable,
            hint: this.hint,
            cause: this.cause ? { name: this.cause.name, message: this.cause.message } : null,
            serverBody: this.serverBody ?? null,
            timestamp: this.timestamp,
        };
    }
}


// ─── Factory: getUserMedia / getDisplayMedia errors ─────────────────────────

/**
 * Convert a DOMException from getUserMedia or getDisplayMedia into a MakedoError.
 *
 * @param {DOMException|Error} err          — The caught browser error
 * @param {'camera'|'screenshare'} source   — Which media API produced it
 * @param {string} [context]                — SDK method name for tracing
 * @returns {MakedoError}
 */
function fromMediaError(err, source = 'camera', context = null) {
    const isScreen = source === 'screenshare';
    const name = err.name || '';

    // ── Permission / user denial ──
    if (name === 'NotAllowedError') {
        // For screenshare, "NotAllowedError" typically means the user hit Cancel
        // on the picker, not a persistent permission block.
        if (isScreen) {
            return new MakedoError(
                ErrorCodes.SCREENSHARE_CANCELLED,
                'Screen share was cancelled or denied.',
                { cause: err, context, retriable: true }
            );
        }
        return new MakedoError(
            ErrorCodes.MEDIA_PERMISSION_DENIED,
            `Camera/microphone permission was denied.`,
            { cause: err, context, retriable: true }
        );
    }

    // ── Device locked by another app ──
    if (name === 'NotReadableError' || name === 'AbortError') {
        if (isScreen) {
            return new MakedoError(
                ErrorCodes.SCREENSHARE_UNKNOWN,
                `Screen share failed: ${err.message}`,
                { cause: err, context, retriable: true }
            );
        }
        return new MakedoError(
            ErrorCodes.MEDIA_DEVICE_BUSY,
            'The camera or microphone is in use by another application.',
            { cause: err, context, retriable: true }
        );
    }

    // ── No device found ──
    if (name === 'NotFoundError') {
        return new MakedoError(
            ErrorCodes.MEDIA_NOT_FOUND,
            'No camera or microphone device was found.',
            { cause: err, context, retriable: false }
        );
    }

    // ── Constraint mismatch ──
    if (name === 'OverconstrainedError') {
        const constraint = err.constraint ? ` (${err.constraint})` : '';
        return new MakedoError(
            ErrorCodes.MEDIA_OVERCONSTRAINED,
            `Requested media constraints cannot be satisfied${constraint}.`,
            { cause: err, context, retriable: true }
        );
    }

    // ── TypeError — usually getDisplayMedia not available ──
    if (name === 'TypeError' && isScreen) {
        return new MakedoError(
            ErrorCodes.SCREENSHARE_UNSUPPORTED,
            'This browser does not support screen sharing.',
            { cause: err, context, retriable: false }
        );
    }

    // ── Fallback ──
    const fallbackCode = isScreen ? ErrorCodes.SCREENSHARE_UNKNOWN : ErrorCodes.MEDIA_UNKNOWN;
    return new MakedoError(
        fallbackCode,
        `Media error: ${err.message || err}`,
        { cause: err, context, retriable: null }
    );
}


// ─── Factory: HTTP response errors ──────────────────────────────────────────

/**
 * Convert an HTTP error response into a MakedoError.
 *
 * @param {number} status       — HTTP status code
 * @param {string} [body]       — Response body or extracted message
 * @param {string} [context]    — SDK method name for tracing
 * @returns {MakedoError}
 */
/**
 * Maps server status strings (from JSON response body) to specific ErrorCodes.
 * The server returns a `status` field in the JSON body for structured rejections.
 * This map is checked BEFORE the generic HTTP-status-code mapping, so the server
 * can provide granular reasons even when different reasons share the same HTTP code.
 */
const SERVER_STATUS_MAP = Object.freeze({
    // -- status field values (crud.jsp and channel operations) --
    'room_not_found':            ErrorCodes.ROOM_NOT_FOUND,
    'context_mismatch':          ErrorCodes.ROOM_NOT_FOUND,  // never reveal existence in another context
    'room_closed':               ErrorCodes.ROOM_CLOSED,
    'room_full':                 ErrorCodes.ROOM_FULL,
    'room_limit_reached':        ErrorCodes.ACCOUNT_LIMIT_REACHED,
    'room_creation_not_allowed': ErrorCodes.AUTH_FORBIDDEN,
    'account_limit_reached':     ErrorCodes.ACCOUNT_LIMIT_REACHED,
    // -- error_code field values (modernized endpoints) --
    'unauthorized':              ErrorCodes.AUTH_FORBIDDEN,
    'invalid_param':             ErrorCodes.INVALID_PARAM,
    'invalid_action':            ErrorCodes.INVALID_PARAM,
    'file_too_large':            ErrorCodes.INVALID_PARAM,
    'invalid_file_type':         ErrorCodes.INVALID_PARAM,
    // Legacy — kept for backward compat with older server responses
    'room_disabled':             ErrorCodes.ROOM_CLOSED,
    'room_pending':              ErrorCodes.ROOM_CLOSED,
});

function fromHttpError(status, body = '', context = null, serverBody = null) {
    // If the server returned a structured JSON body with a `status` field,
    // use it for granular error mapping before falling back to HTTP status code.
    if (serverBody?.status && SERVER_STATUS_MAP[serverBody.status]) {
        return new MakedoError(
            SERVER_STATUS_MAP[serverBody.status],
            body || serverBody.error || `Server rejected request (${serverBody.status}).`,
            { context, retriable: false, subcode: serverBody.status }
        );
    }

    if (status === 401) {
        return new MakedoError(
            ErrorCodes.AUTH_SESSION_EXPIRED,
            body || 'Session expired (HTTP 401).',
            { context, retriable: true }
        );
    }
    if (status === 403) {
        // project_unavailable and project_expired are resource-availability errors, not generic auth failures.
        // They get their own error code so callers can handle them distinctly.
        if (serverBody?.diag === 'project_unavailable') {
            return new MakedoError(
                ErrorCodes.PROJECT_UNAVAILABLE,
                body || 'The project was not found or is no longer available.',
                { context, retriable: false, subcode: 'project_unavailable' }
            );
        }
        if (serverBody?.diag === 'project_expired') {
            const _expiredErr = new MakedoError(
                ErrorCodes.PROJECT_UNAVAILABLE,
                body || 'This project\'s trial period has expired.',
                { context, retriable: false, subcode: 'project_expired' }
            );
            _expiredErr.hint = 'This project has expired. Upgrade your plan in the vibelive dashboard to reactivate it.';
            return _expiredErr;
        }
        if (serverBody?.diag === 'project_archived' || serverBody?.diag === 'context_archived') {
            // 'project_archived' emitted by updated server; 'context_archived' kept for older server versions.
            const _archivedErr = new MakedoError(
                ErrorCodes.PROJECT_UNAVAILABLE,
                body || 'This project has been archived.',
                { context, retriable: false, subcode: 'project_archived' }
            );
            _archivedErr.hint = 'This project has been archived. Contact the account administrator to restore it in the vibelive dashboard.';
            return _archivedErr;
        }
        if (serverBody?.diag === 'project_disabled') {
            // 'project_disabled' emitted by updated server; 'context_disabled' in _diagHints below for older versions.
            const _disabledErr = new MakedoError(
                ErrorCodes.PROJECT_UNAVAILABLE,
                body || 'This project has been disabled.',
                { context, retriable: false, subcode: 'project_disabled' }
            );
            _disabledErr.hint = 'This project has been disabled by its owner. Contact the account administrator to reactivate it.';
            return _disabledErr;
        }
        // Normalize diag to subcode, trimming embedded IDs from the long holds_context variant.
        const _subcode = serverBody?.diag
            ? (serverBody.diag.startsWith('holds_context_false_in_access_limits')
                ? 'holds_context_false_in_access_limits'
                : serverBody.diag)
            : null;
        const _forbiddenErr = new MakedoError(
            ErrorCodes.AUTH_FORBIDDEN,
            body || 'Authorization denied (HTTP 403).',
            { context, retriable: false, subcode: _subcode }
        );
        // Override the generic hint with a diag-specific message when the server provides one.
        // All diag codes are emitted by authentication.jsp and accessLimits.jsp.
        // The raw diag string is always available on error.serverBody.diag for logging.
        if (serverBody?.diag) {
            const _diagHints = {
                // ── Project / context config problems ──
                'project_not_active':       'The projectId exists but is not active. It may have been disabled. Check the vibelive dashboard.',
                'no_project_key_presented': 'A projectKey is required for this project. Pass it to VibeLive.init({ projectId, projectKey: "..." }).',
                'project_key_rejected':     'The projectKey was rejected. Verify it in the vibelive dashboard and ensure it matches this projectId.',
                // ── Context status problems (legacy diag values from older server versions) ──
                'context_disabled':         'This project has been disabled by its owner. Contact the account administrator to reactivate it.',
                // context_archived / project_archived / project_disabled are handled above — return PROJECT_UNAVAILABLE
                // ── Origin whitelist problems ──
                'origin_not_whitelisted':   'Requests from this origin are blocked by the project\'s allowed-origins list. Add your domain in the vibelive dashboard.',
                // ── Token / session problems ──
                'token_not_found_in_db':    'The auth token is invalid or has expired. Call login() or signup() to obtain a fresh session.',
                'ctx_token_mismatch':       'The auth token is scoped to a different project. Ensure users are authenticated using this projectId.',
                // ── Membership problems ──
                'not_holding_context_in_auth_jsp': 'The authenticated user is not a member of this project. Ensure the user was created using this projectId.',
            };
            // holds_context_false_in_access_limits carries embedded IDs after a colon — match by prefix.
            const _hint = _diagHints[serverBody.diag]
                ?? (serverBody.diag.startsWith('holds_context_false_in_access_limits')
                    ? 'The user account does not have access to this project. Verify the user was provisioned for this projectId.'
                    : null);
            if (_hint) _forbiddenErr.hint = _hint;
        }
        return _forbiddenErr;
    }
    if (status === 404) {
        return new MakedoError(
            ErrorCodes.ROOM_NOT_FOUND,
            body || 'Resource not found (HTTP 404).',
            { context, retriable: false }
        );
    }
    if (status === 0 || status === undefined) {
        return new MakedoError(
            ErrorCodes.NETWORK_OFFLINE,
            'Network request failed. Check your internet connection.',
            { context, retriable: true }
        );
    }
    // For all other status codes (400, 409, 422, 500, etc.), check error_code from the response body.
    if (serverBody?.error_code && SERVER_STATUS_MAP[serverBody.error_code]) {
        return new MakedoError(
            SERVER_STATUS_MAP[serverBody.error_code],
            body || serverBody.error || `Server error (${serverBody.error_code}).`,
            { context, retriable: false, subcode: serverBody.error_code }
        );
    }
    return new MakedoError(
        ErrorCodes.NETWORK_HTTP_ERROR,
        body || `HTTP error ${status}.`,
        { context, retriable: null }
    );
}


// ─── Exports ────────────────────────────────────────────────────────────────

export { MakedoError, ErrorCodes, fromMediaError, fromHttpError };
export default MakedoError;
